import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, X, Loader2, ArrowRight } from "lucide-react";
import AuthImage from "../components/AuthImage";
import SearchableSelect from "../components/SearchableSelect";
import ConfirmDialog from "../components/ConfirmDialog";
import { useAuth } from "../lib/auth";

export default function AddStock() {
  const nav = useNavigate();
  const { user, can } = useAuth();
  const [cats, setCats] = useState([]);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [lotNumbers, setLotNumbers] = useState([]);
  const [racks, setRacks] = useState([]);
  const [factories, setFactories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [f, setF] = useState({
    category_id: "", color_id: "", size_id: "",
    lot_no: "", quantity: "", rack_no: "",
    factory_no: "", warehouse_place: "",
    stone_block_no: "",
    employee_name: user?.full_name || "", notes: "",
  });
  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState({});
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    api.get("/categories").then(r => setCats(r.data));
    api.get("/warehouses").then(r => setWarehouses(r.data));
    api.get("/lot_numbers").then(r => setLotNumbers(r.data));
    api.get("/factories").then(r => setFactories(r.data));
    api.get("/custom-fields", { params: { form_scope: "add_lot" } })
      .then(r => setCustomFields((r.data || []).filter(fd => !fd.hidden)))
      .catch(() => {});
  }, []);

  // Rack list is scoped to the currently selected warehouse — swap out the
  // options whenever the warehouse dropdown changes so operators can't pick a
  // rack from a warehouse they aren't allocating stock into.
  useEffect(() => {
    const params = f.warehouse_place ? { warehouse: f.warehouse_place } : {};
    api.get("/racks", { params }).then(r => {
      setRacks(r.data);
      // If the previously selected rack is no longer valid for this warehouse,
      // clear the selection so the operator picks a fresh one.
      if (f.rack_no && !r.data.some(x => x.name === f.rack_no)) {
        setF(prev => ({ ...prev, rack_no: "" }));
      }
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [f.warehouse_place]);

  useEffect(() => {
    if (!f.category_id) { setColors([]); setSizes([]); return; }
    api.get("/colors", { params: { category_id: f.category_id } }).then(r => setColors(r.data));
    api.get("/sizes", { params: { category_id: f.category_id } }).then(r => setSizes(r.data));
  }, [f.category_id]);

  const currentCat = useMemo(() => cats.find(c => c.id === f.category_id), [cats, f.category_id]);
  const minPhotos = currentCat?.min_photos || 1;
  const photoLabels = currentCat?.photo_labels || [];

  const onUpload = async (e, slot) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // Preserve exact device selection order: `slot` is the 0-indexed
      // position the user picked this photo for, `sequence_index` is 1-based.
      const fd = new FormData();
      fd.append("file", file);
      fd.append("batch_no", f.lot_no || "NOBATCH");
      fd.append("stage", "lot");
      fd.append("index", String(slot + 1));
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos(p => {
        const copy = [...p];
        copy[slot] = { storage_path: r.data.storage_path,
                       label: photoLabels[slot] || `Photo ${slot+1}`,
                       sequence_index: r.data.sequence_index };
        return copy;
      });
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const removePhoto = (i) => setPhotos(p => p.filter((_, idx) => idx !== i));

  const tryOpenConfirm = async (e) => {
    e.preventDefault();
    const validPhotos = photos.filter(Boolean);
    if (validPhotos.length < minPhotos) {
      toast.error(`Upload at least ${minPhotos} photo${minPhotos>1?'s':''} for ${currentCat?.name}`);
      return;
    }
    // Real-time uniqueness: (category, color, size, lot_no) must be unique
    if (f.category_id && f.color_id && f.size_id && f.lot_no.trim()) {
      try {
        const r = await api.post("/lots/check-uniqueness", {
          category_id: f.category_id, color_id: f.color_id,
          size_id: f.size_id, lot_no: f.lot_no.trim(),
        });
        if (r.data.exists) {
          const existing = r.data.lot;
          const ok = window.confirm(
            `Lot ${existing.lot_no} already exists for ${existing.category_name} · ${existing.color_name} · ${existing.size_label} ` +
            `(currently ${existing.quantity} pcs on Rack ${existing.rack_no}). ` +
            `Add quantity to this existing lot instead? Click Cancel to clear and enter a different lot number.`);
          if (ok) { nav(`/lot/${existing.id}?append=1`); return; }
          setF(x => ({ ...x, lot_no: "" }));
          return;
        }
      } catch (err) { console.debug("uniqueness check failed:", err); }
    }
    setConfirmOpen(true);
  };

  const doSubmit = async () => {
    setBusy(true);
    try {
      // Validate required custom fields
      for (const cf of customFields) {
        if (cf.required && (customValues[cf.key] === undefined || customValues[cf.key] === "" || customValues[cf.key] === null)) {
          toast.error(`${cf.label} is required`); setBusy(false); return;
        }
      }
      const validPhotos = photos.filter(Boolean);
      const r = await api.post("/lots", {
        ...f, quantity: parseInt(f.quantity, 10), photos: validPhotos,
        custom_fields: customValues,
      });
      toast.success(`Lot ${r.data.lot_no} saved · Printing ${r.data.quantity} labels…`);
      setConfirmOpen(false);
      nav(`/labels?lot=${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create lot");
    } finally { setBusy(false); }
  };

  const opt = (arr) => arr.map(x => ({ value: x.id, label: x.name, sub: x.note }));

  const canAddLot = can("settings.lots");
  const canAddRack = can("settings.racks");
  const canAddFactory = can("settings.factories");
  const canAddWarehouse = can("settings.warehouses");

  return (
    <div className="space-y-8" data-testid="add-lot-page">
      <header>
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-2">New arrival</div>
        <h1 className="font-display font-bold text-4xl tracking-tight text-[#2D2622]">Add a new lot.</h1>
        <p className="text-[#5D5148] mt-2 text-sm max-w-xl">
          Pick your category, choose or search a lot & rack, take photos, and we'll print the labels for you.
        </p>
      </header>

      <form onSubmit={tryOpenConfirm} className="space-y-8">
        <section className="bg-white border border-[#E5D8C4] p-5 space-y-4">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Product</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Category">
              <SearchableSelect testId="add-category" value={f.category_id}
                onChange={(v) => setF({ ...f, category_id: v, color_id: "", size_id: "" })}
                options={cats.map(c => ({ value: c.id, label: c.name }))}
                placeholder="Choose category…" />
            </Field>
            <Field label="Colour">
              <SearchableSelect testId="add-color" value={f.color_id}
                onChange={(v) => setF({ ...f, color_id: v })}
                options={colors.map(c => ({ value: c.id, label: c.name }))}
                placeholder="Choose colour…"
                disabled={!f.category_id} emptyMessage="Pick a category first" />
            </Field>
            <Field label="Size">
              <SearchableSelect testId="add-size" value={f.size_id}
                onChange={(v) => setF({ ...f, size_id: v })}
                options={sizes.map(s => ({ value: s.id, label: s.label }))}
                placeholder="Choose size…"
                disabled={!f.category_id} emptyMessage="Pick a category first" />
            </Field>
          </div>
        </section>

        <section className="bg-white border border-[#E5D8C4] p-5 space-y-4">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Lot details</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Lot No." hint={canAddLot ? "Manage in Settings" : "Ask admin to add more"}>
              <SearchableSelect testId="add-lotno" value={f.lot_no}
                onChange={(v) => setF({ ...f, lot_no: v })}
                options={lotNumbers.map(l => ({ value: l.name, label: l.name }))}
                placeholder="Select lot no…" emptyMessage="No lots defined" />
            </Field>
            <Field label="Quantity">
              <input required data-testid="add-qty" type="number" min="1" placeholder="pcs" value={f.quantity}
                onChange={(e) => setF({ ...f, quantity: e.target.value })}
                className="h-11 px-3 bg-white border border-[#E5D8C4] text-sm w-full font-mono2 focus:outline-none focus:border-[#2D2622]" />
            </Field>
            <Field label="Rack No." hint="Type A/B/C… to jump">
              <SearchableSelect testId="add-rack" value={f.rack_no}
                onChange={(v) => setF({ ...f, rack_no: v })}
                options={racks.map(r => ({ value: r.name, label: r.name }))}
                placeholder="Select rack…" emptyMessage="No racks defined" />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Factory">
              <SearchableSelect testId="add-factory" value={f.factory_no}
                onChange={(v) => setF({ ...f, factory_no: v })}
                options={factories.map(x => ({ value: x.name, label: x.name, sub: x.note }))} placeholder="Select factory…" />
            </Field>
            <Field label="Warehouse Place">
              <SearchableSelect testId="add-warehouse" value={f.warehouse_place}
                onChange={(v) => setF({ ...f, warehouse_place: v })}
                options={warehouses.map(x => ({ value: x.name, label: x.name, sub: x.note }))} placeholder="Select warehouse…" />
            </Field>
            <Field label="Employee Name">
              <input required data-testid="add-emp" placeholder="Full name" value={f.employee_name}
                onChange={(e) => setF({ ...f, employee_name: e.target.value })}
                className="h-11 px-3 bg-white border border-[#E5D8C4] text-sm w-full focus:outline-none focus:border-[#2D2622]" />
            </Field>
          </div>
          <Field label="Notes (optional)">
            <textarea data-testid="add-notes" rows={2} value={f.notes}
              onChange={(e) => setF({ ...f, notes: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-[#E5D8C4] text-sm focus:outline-none focus:border-[#2D2622]" />
          </Field>
          <Field label="Stone Block Number (optional)" hint="Add later if not available yet">
            <input data-testid="add-stone-block" placeholder="e.g. SB-1234" value={f.stone_block_no}
              onChange={(e) => setF({ ...f, stone_block_no: e.target.value })}
              className="h-11 px-3 bg-white border border-[#E5D8C4] text-sm w-full font-mono2 focus:outline-none focus:border-[#2D2622]" />
          </Field>
          {customFields.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-[#F3ECDF]">
              {customFields.map(cf => (
                <Field key={cf.id} label={`${cf.label}${cf.required ? " *" : ""}`}>
                  <CustomFieldInput def={cf}
                    value={customValues[cf.key]}
                    onChange={(v) => setCustomValues(x => ({ ...x, [cf.key]: v }))} />
                </Field>
              ))}
            </div>
          )}
        </section>

        {currentCat && (
          <section className="bg-white border border-[#E5D8C4] p-5">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Photos</div>
                <div className="text-sm text-[#2D2622] mt-1">
                  {currentCat.name} requires <span className="font-mono2 font-semibold">{minPhotos}</span> minimum
                </div>
              </div>
              <div className="text-xs font-mono2 text-[#8B6F47]">{photos.filter(Boolean).length}/{minPhotos}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Array.from({ length: Math.max(minPhotos, photos.length + (photos.length < minPhotos ? 0 : 1)) }).map((_, i) => {
                const p = photos[i];
                const label = photoLabels[i] || `Photo ${i+1}`;
                return (
                  <div key={p?.storage_path || `slot-${i}`} className="border border-[#E5D8C4] bg-white">
                    <div className="aspect-[4/3] bg-[#FAF7F2] relative flex items-center justify-center">
                      {p ? (
                        <>
                          <AuthImage path={p.storage_path} className="w-full h-full object-cover" />
                          <button type="button" onClick={() => removePhoto(i)} data-testid={`remove-photo-${i}`}
                            className="absolute top-2 right-2 w-7 h-7 bg-white/90 border border-[#E5D8C4] flex items-center justify-center hover:bg-red-50 hover:border-red-300">
                            <X size={13}/>
                          </button>
                        </>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-2 p-2">
                          {uploading ? (
                            <Loader2 className="animate-spin" size={20}/>
                          ) : (
                            <Camera size={22} strokeWidth={1.5} className="text-[#8B6F47]"/>
                          )}
                          <div className="flex flex-col sm:flex-row gap-1.5 w-full max-w-[220px]">
                            <label className="flex-1 h-8 flex items-center justify-center gap-1 border border-[#E5D8C4] bg-white text-[10px] uppercase tracking-widest cursor-pointer hover:border-[#2D2622] text-[#2D2622]">
                              📷 Camera
                              <input type="file" accept="image/*" capture="environment" className="hidden"
                                data-testid={`photo-camera-${i}`} onChange={(e) => onUpload(e, i)} />
                            </label>
                            <label className="flex-1 h-8 flex items-center justify-center gap-1 border border-[#E5D8C4] bg-white text-[10px] uppercase tracking-widest cursor-pointer hover:border-[#2D2622] text-[#2D2622]">
                              📁 Choose
                              <input type="file" accept="image/*" className="hidden"
                                data-testid={`photo-file-${i}`} onChange={(e) => onUpload(e, i)} />
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-3 text-xs text-[#2D2622] font-medium">{label}</div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="flex justify-end pt-2">
          <button type="submit" disabled={busy} data-testid="add-submit"
            className="inline-flex items-center gap-2 h-12 px-6 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] hover:bg-[#5D4A2E] disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={14}/> : <>Save & print labels <ArrowRight size={14}/></>}
          </button>
        </div>
      </form>

      <ConfirmDialog open={confirmOpen} onCancel={() => setConfirmOpen(false)}
        onConfirm={doSubmit} loading={busy}
        title="Confirm new lot"
        message={<>
          You're about to add <b>{f.quantity || 0} pcs</b> to Rack <b>{f.rack_no || "—"}</b> under Lot <b>{f.lot_no || "—"}</b>.
          {" "}<b>{f.quantity || 0}</b> labels will be prepared for printing. Proceed?
        </>}
        confirmLabel="Yes, save & print" />
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="flex justify-between items-baseline">
        <label className="text-[10px] font-semibold text-[#8B6F47] uppercase tracking-[0.25em] mb-2 block">{label}</label>
        {hint && <span className="text-[10px] text-[#A8998A]">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

/** Renders a single dynamic custom field defined in Settings → Custom Fields. */
function CustomFieldInput({ def, value, onChange }) {
  const base = "h-11 px-3 bg-white border border-[#E5D8C4] text-sm w-full focus:outline-none focus:border-[#2D2622]";
  const testId = `custom-${def.key}`;
  if (def.field_type === "number") {
    return <input type="number" data-testid={testId} value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      className={`${base} font-mono2`} />;
  }
  if (def.field_type === "date") {
    return <input type="date" data-testid={testId} value={value || ""}
      onChange={(e) => onChange(e.target.value)} className={base} />;
  }
  if (def.field_type === "dropdown") {
    return (
      <select data-testid={testId} value={value || ""}
        onChange={(e) => onChange(e.target.value)} className={base}>
        <option value="">— select —</option>
        {(def.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (def.field_type === "checkbox") {
    return (
      <label className="inline-flex items-center gap-2 h-11">
        <input type="checkbox" data-testid={testId} checked={!!value}
          onChange={(e) => onChange(e.target.checked)} />
        <span className="text-sm text-[#5D5148]">Yes</span>
      </label>
    );
  }
  return <input type="text" data-testid={testId} value={value || ""}
    onChange={(e) => onChange(e.target.value)} className={base} />;
}
